import React from 'react';
import { CheckCircle, Target, Gauge, Clock, Timer, Moon, Scissors } from 'lucide-react';

// Format decimal hours to HH:MM format
const formatHoursToHHMM = (decimalHours) => {
  if (!decimalHours || isNaN(decimalHours)) return '00:00';
  const totalMinutes = Math.round(decimalHours * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Round number to 2 decimal places
const round2 = (num) => {
  if (!num || isNaN(num)) return 0;
  return Math.round(num * 100) / 100;
};

function KPICards({ summary, settings, t }) {
  if (!summary) return null;

  const efficiencyTarget = settings?.efficiencyTarget || 35;
  const entreCorteTarget = settings?.entreCorteTarget || 2;
  const speedTarget = settings?.speedTarget || 10;

  const {
    total_cutting_time_hours = 0, total_real_time_hours = 0, efficiency = 0,
    avg_entre_cortes = 0, avg_speed = 0,
    total_machine_stopped_hours = 0, total_days = 0,
    total_cutting_time_min = 0, total_machine_stopped_min = 0, total_perimeter = 0,
    total_cuts = 0, total_between_markers_min = 0,
    total_time_outside_hours_hours = 0, total_time_outside_hours_min = 0,
  } = summary;

  // Weighted average speed from daily data
  const dailySummary = summary.daily_summary || [];
  const weightedSpeedSum = dailySummary.reduce((sum, d) => sum + ((d.weighted_avg_speed || 0) * (d.total_cutting_time || 0)), 0);
  const totalCuttingForSpeed = dailySummary.reduce((sum, d) => sum + (d.total_cutting_time || 0), 0);
  const weightedAvgSpeed = totalCuttingForSpeed > 0 ? round2(weightedSpeedSum / totalCuttingForSpeed) : round2(avg_speed);

  const avgBetweenMarkers = total_cuts > 0 ? round2(total_between_markers_min / total_cuts) : 0;

  // Efficiency = Corte / (Corte + Intra)
  const corteIntraTotal = total_cutting_time_min + total_machine_stopped_min;
  const calculatedEfficiency = corteIntraTotal > 0 ? round2((total_cutting_time_min / corteIntraTotal) * 100) : 0;

  const efficiencyDiff = calculatedEfficiency - efficiencyTarget;
  const entreCortesDiff = entreCorteTarget - round2(avg_entre_cortes);
  const speedDiff = weightedAvgSpeed - speedTarget;

  const efficiencyMet = calculatedEfficiency >= efficiencyTarget;
  const entreCortesMet = avg_entre_cortes <= entreCorteTarget;
  const speedMet = weightedAvgSpeed >= speedTarget;

  const daysAnalyzed = dailySummary.length || total_days || 0;

  const cards = [
    { 
      label: "EFICIÊNCIA", 
      value: `${calculatedEfficiency}%`, 
      sub: `Meta: ${efficiencyTarget}%`,
      diff: `${efficiencyDiff >= 0 ? '+' : ''}${round2(efficiencyDiff)}pp`,
      icon: efficiencyMet ? CheckCircle : Target, 
      color: efficiencyMet ? "emerald" : "red",
      met: efficiencyMet,
      tooltip: "Corte / (Corte + Intra Marc.)"
    },
    { 
      label: "VEL. REAL POND.", 
      value: `${weightedAvgSpeed} m/min`, 
      sub: `Meta: ${speedTarget} m/min`,
      diff: `${speedDiff >= 0 ? '+' : ''}${round2(speedDiff)}`,
      icon: speedMet ? CheckCircle : Gauge, 
      color: speedMet ? "emerald" : "amber",
      met: speedMet,
      tooltip: "Velocidade ponderada pelo tempo de corte"
    },
    { 
      label: "PARAGEM MÉDIA", 
      value: `${round2(avg_entre_cortes)} min`, 
      sub: `Meta: ≤${entreCorteTarget} min`,
      diff: `${entreCortesDiff >= 0 ? '+' : ''}${round2(entreCortesDiff)}`,
      icon: entreCortesMet ? CheckCircle : Clock, 
      color: entreCortesMet ? "emerald" : "amber",
      met: entreCortesMet
    },
    { 
      label: "ENTRE MARCADAS", 
      value: `${avgBetweenMarkers} min`, 
      sub: `Média por corte`,
      diff: null,
      icon: Timer, 
      color: "sky",
      met: null
    },
    { 
      label: "MÁQ. PARADA", 
      value: formatHoursToHHMM(total_machine_stopped_hours), 
      sub: `de ${formatHoursToHHMM(total_real_time_hours)} total`,
      diff: null,
      icon: Clock, 
      color: "amber",
      met: null
    },
    { 
      label: "HORAS CORTE", 
      value: formatHoursToHHMM(total_cutting_time_hours), 
      sub: `${daysAnalyzed} dia(s)`,
      diff: null,
      icon: Scissors, 
      color: "sky",
      met: null
    },
    { 
      label: "FORA HORÁRIO", 
      value: formatHoursToHHMM(total_time_outside_hours_hours), 
      sub: `${Math.round(total_time_outside_hours_min || 0)} min total`,
      diff: null,
      icon: Moon, 
      color: total_time_outside_hours_min > 0 ? "amber" : "emerald",
      met: null,
      tooltip: "Tempo fora do horário (antes 8h / após 17:30)"
    },
  ];

  const iconMap = {
    sky: "text-sky-500 bg-sky-500/10 border-sky-500/20",
    amber: "text-amber-500 bg-amber-500/10 border-amber-500/20",
    emerald: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20",
    red: "text-red-500 bg-red-500/10 border-red-500/20",
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4" data-testid="kpi-cards-grid">
      {cards.map((card, i) => {
        const Icon = card.icon;
        return (
          <div key={i} className={`${t.card} rounded-lg p-4 transition-colors`} title={card.tooltip || ''}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-md flex items-center justify-center border ${iconMap[card.color]}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <span className={`text-[9px] uppercase tracking-[0.12em] font-medium ${t.textMut}`}>{card.label}</span>
              </div>
              {card.met !== null && (
                <div className={`text-[9px] font-medium ${card.met ? 'text-emerald-500' : 'text-red-400'}`}>
                  {card.met ? '✓' : '✗'}
                </div>
              )}
            </div>
            <div className={`text-xl font-semibold ${t.text} tracking-tight`} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              {card.value}
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className={`text-[10px] ${t.textMut}`}>{card.sub}</span>
              {card.diff && (
                <span className={`text-[10px] font-medium ${card.met ? 'text-emerald-500' : 'text-red-400'}`}>
                  {card.diff}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default KPICards;
